import React from 'react';
import { Box, Button, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../constants/language';
import useDebounce from '../../hooks/useDebounce';

interface SearchInputProps {
  value?: string;
  onChange?: (val: string) => void;
  onSearch: (val: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
}

export default function SearchInput({ value, onChange, onSearch, placeholder, className, debounceMs = 400 }: SearchInputProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState<string>(value || '');
  const firstRun = React.useRef(true);

  React.useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const debouncedValue = useDebounce(inputValue, debounceMs ?? 400);

  const onSearchRef = React.useRef(onSearch);

  React.useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  React.useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    onSearchRef.current(debouncedValue);
  }, [debouncedValue]);

  React.useEffect(() => {
    if (inputValue === '') {
      onSearchRef.current('');
    }
  }, [inputValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchRef.current(inputValue);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      className={className}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder={placeholder || 'Search'}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (onChange) onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: { px: 1.5, height: 40, fontSize: 14 },
        }}
        sx={{ flex: 1 }}
      />
      <Button
        onClick={() => onSearchRef.current(inputValue)}
        variant="contained"
        size="small"
        sx={(theme) => ({
          borderRadius: 0,
          height: 40,
          minWidth: 80,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#F2FBF3',
          color: '#296C32',
          '&:hover': {
            bgcolor: '#F2FBF3',
          },
        })}
        aria-label="search"
      >
        <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>
          {t(LANG_TEXT.SEARCH)}
        </Box>
        <SearchIcon fontSize="small" />
      </Button>
    </Box>
  );
}
